<template>
  <div class="relative">
    <AppInputLabel
      :name="name"
      :label="label"
      :hasError="hasError(name).value"
      :required="required"
      :rightAlign="rightAlign"
    />
    <div>
      <input
        :id="name"
        class="w-full"
        :class="{
          'border-critial': hasError(name).value,
          'text-right': rightAlign
        }"
        :disabled="disabled"
        v-model="formattedTime"
        type="text"
        readonly
        @click="showPicker = true"
      />
    </div>
    <div
      v-if="showPicker"
      v-click-outside="() => showPicker = false"
      class="absolute z-50 mt-1 bg-white shadow-lg rounded-md border"
      :class="{ 'right-0': rightAlign }"
    >
      <TimePicker
        :time="innerValue"
        :disabled="disabled"
        @done="handleTimeChange"
        :rightAlign="rightAlign"
        :class="{ 'border-critial': hasError(name).value }"
      />
    </div>
    <div>
      <AppInputError v-show="hasError(name)" :error="errors.get(name)" />
    </div>
  </div>
</template>
<script setup lang="ts">
import { inject, ref, watch, onBeforeUnmount, onMounted, onUnmounted, computed, reactive } from "vue";
import { ValidationContextKey } from "@/models/Context";
import useFormValidation from "@/modules/useFormValidation";
import { vClickOutside } from '@/directives/clickOutside';
import AppInputLabel from "./AppInputLabel.vue";
import AppInputError from "./AppInputError.vue";
import TimePicker from "./TimePicker.vue";
import useDate from "@/modules/useDate";

const props = defineProps<{
  required: boolean;
  name: string;
  label: string;
  modelValue?: string;
  disabled?: boolean;
  rightAlign?: boolean; // New prop
}>();

const { formatTime } = useDate();

const innerValue = ref(formatTime(props.modelValue ?? "00:00"));
const showPicker = ref(false);
const emit = defineEmits(["update:modelValue"]);

// Create a default validation context if none is provided
const defaultContext = {
  errors: new Map<string, string>(),
  validators: {} as Record<string, (key: string, validator: string) => void>,
  registerValidator: (key: string, validator: (key: string, validator: string) => void) => {
    defaultContext.validators[key] = validator;
  }
};

const context = inject(ValidationContextKey, defaultContext);
const { validateRequiredField, clearValidator, clearError, errors, registerValidator, hasError } = useFormValidation(context);

const formattedTime = computed(() => formatTime(innerValue.value));


watch(
  () => props.modelValue,
  (newVal) => {
    if (newVal && newVal !== innerValue.value) {
      innerValue.value = newVal;
    }
  },
  { immediate: true }
);

const handleTimeChange = (time: string) => {
  innerValue.value = time;
  emit("update:modelValue", time);
  showPicker.value = false;
};

onBeforeUnmount(() => {
  clearValidator(props.name);
  clearError(props.name);
});
</script>
<style scoped>
.time-picker {
  width: 200px;
}
</style>
